import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQueries } from 'react-query';
import { getFrameworks, getControlsGrouped, postGapAnalysis } from 'services/gap-analysis';
import { Main as Container } from 'Components/Main';
import React, { useEffect, useState, useMemo } from 'react';
import FrameworkSelector from 'Components/FrameworkSelector';
import DomainControlsList from 'Components/DomainControlsList';
import { useNavigate } from 'react-router-dom';
import { PageHelper } from 'Components/PageHelper';
import { PageHeader, TextField } from 'Components/ui';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const GREEN = 'var(--vo-success)';
const GREEN_DARK = 'color-mix(in srgb, var(--vo-success) 80%, black)';
const BG = 'var(--vo-mist)';
const TEXT = 'var(--vo-ink-2)';
const BORDER_IDLE = 'var(--vo-steel)';
const DIVIDER = 'var(--vo-cloud)';

/* ── Stepper card ── */
const StepperCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 20px;
`;
const StepperWrap = styled.div`
  display: flex;
  align-items: flex-start;
`;
const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;
const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  background: ${({ $s }) => $s === 'active' ? BLUE : $s === 'done' ? GREEN : DIVIDER};
  color: ${({ $s }) => $s === 'inactive' ? BORDER_IDLE : 'white'};
  flex-shrink: 0;
`;
const StepLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: ${({ $s }) => $s === 'active' ? 600 : 400};
  color: ${({ $s }) => $s === 'active' ? BLUE : $s === 'done' ? GREEN : BORDER_IDLE};
  white-space: nowrap;
  text-align: center;
  max-width: 72px;
  margin: 0;
`;
const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${({ $done }) => $done ? GREEN : DIVIDER};
  margin: 0 4px;
  margin-top: 16px;
`;

/* ── Form card ── */
const FormCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  ${({ $wide }) => !$wide && 'max-width: 600px;'}
`;
const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 4px;
`;
const CardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0 0 20px;
`;

/* ── Step 0 fields ── */
const FieldsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 20px;
`;
const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
`;
const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT};
  margin-bottom: 6px;
`;
const ErrorMsg = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-danger);
  margin-top: 4px;
`;

/* ── Summary step ── */
const SummaryList = styled.div`
  margin-top: 20px;
`;
const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  padding: 12px 0;
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
`;
const SummaryLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .06em;
  margin: 0;
`;
const SummaryValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${INK};
  font-weight: 500;
  margin: 0;
`;
const FwPillsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const FwPill = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${BLUE};
  background: var(--vo-capri-50);
  padding: 3px 10px;
  border-radius: 20px;
`;

/* ── Navigation buttons ── */
const NavRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;
const BackBtn = styled.button`
  height: 44px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  &:hover { background: ${BG}; }
`;
const NextBtn = styled.button`
  height: 44px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  &:hover { background: ${BLUE_DEEP}; }
`;
const SaveBtn = styled.button`
  height: 44px;
  background: ${GREEN};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  &:hover { background: ${GREEN_DARK}; }
`;

const CheckSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const GapAnalysisForm = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [controls, setControls] = useState([]);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
    unregister,
  } = useForm({
    defaultValues: {
      company_id: selectedTenant.slug || '',
      asset_id: '',
      name: '',
      description: '',
      type: 'company',
      frameworks: [],
    },
  });

  useEffect(() => {
    if (selectedTenant) setValue('company_id', selectedTenant.slug);
  }, [selectedTenant, setValue]);

  const results = useQueries([
    {
      queryKey: ['frameworks'],
      queryFn: getFrameworks,
    },
  ]);

  const frameworksData = results[0].data?.data || [];

  useEffect(() => {
    const fetchControlsGrouped = async frameworksIds => {
      try {
        if (frameworksIds.length === 0) { setControls([]); return; }
        const result = await getControlsGrouped(frameworksIds);
        if (result.status === 200) setControls(result.data);
      } catch (error) {
        console.error('Erro ao buscar controles agrupados:', error);
      }
    };
    fetchControlsGrouped(selectedFrameworks.map(fw => fw._id));
  }, [selectedFrameworks]);

  useEffect(() => {
    if (step === 1) {
      register('frameworks', {
        validate: value => (Array.isArray(value) && value.length > 0) || t('gap-analysis:form.validation.frameworkRequired'),
      });
      if (!watch('frameworks') || watch('frameworks').length === 0) {
        setValue('frameworks', selectedFrameworks.map(fw => fw._id));
      }
    } else if (step === 0 && selectedFrameworks.length === 0) {
      unregister('frameworks');
    }
  }, [register, unregister, step, setValue, watch, selectedFrameworks, t]);

  const handleToggle = framework => {
    setSelectedFrameworks(prevSelected => {
      const isSelected = prevSelected.some(item => item._id === framework._id);
      const updatedSelected = isSelected
        ? prevSelected.filter(item => item._id !== framework._id)
        : [...prevSelected, framework];
      setValue('frameworks', updatedSelected.map(fw => fw._id));
      trigger('frameworks');
      return updatedSelected;
    });
  };

  const submitGapAnalysis = handleSubmit(async data => {
    try {
      const payload = { ...data, framework_ids: selectedFrameworks.map(fw => fw._id) };
      delete payload.frameworks;
      const response = await postGapAnalysis(payload);
      const analysisId = response.data?._id || response.data?.id;
      if ((response.status === 201 || response.status === 200) && analysisId) {
        navigate(`/gap-analysis/show/${analysisId}`);
      }
    } catch (error) {
      alert(t('gap-analysis:form.saveError'));
    }
  });

  const handleNext = async () => {
    let isValid = await trigger();
    if (step === 1) {
      const frameworksValid = await trigger('frameworks');
      isValid = isValid && frameworksValid;
    }
    if (isValid) setStep(prev => prev + 1);
  };

  const steps = [
    t('gap-analysis:form.steps.step1'),
    t('gap-analysis:form.steps.step2'),
    t('gap-analysis:form.steps.step3'),
    t('gap-analysis:form.steps.step4'),
  ];

  const tourSteps = useMemo(() => [
    { target: 'body', content: t('gap-analysis:form.tour.step0'), disableBeacon: true, disableScrolling: true, placement: 'center' },
    { target: '[data-tour="page-title"]', content: t('gap-analysis:form.tour.step1'), disableScrolling: true },
    { target: '[data-tour="stepper-card"]', content: t('gap-analysis:form.tour.step2'), disableScrolling: false },
    { target: '[data-tour="gap-name"]', content: t('gap-analysis:form.tour.step3'), disableScrolling: false },
    { target: '[data-tour="gap-description"]', content: t('gap-analysis:form.tour.step4'), disableScrolling: false },
    { target: 'body', content: t('gap-analysis:form.tour.step5'), disableScrolling: true, placement: 'center' },
    { target: 'body', content: t('gap-analysis:form.tour.step6'), disableScrolling: true, placement: 'center' },
    { target: 'body', content: t('gap-analysis:form.tour.step7'), disableScrolling: true, placement: 'center' },
    { target: 'body', content: t('gap-analysis:form.tour.step8'), disableScrolling: true, placement: 'center' },
  ], [t]);

  return (
    <Container>
      <PageHelper steps={tourSteps} tourKey="gap-analysis-form" />

      <PageHeader
        data-tour="page-title"
        title={t('gap-analysis:form.pageTitle')}
        segments={[
          { label: t('common:breadcrumbs.analysis') },
          { label: t('gap-analysis:page.title'), route: '/gap-analysis' },
          { label: t('gap-analysis:form.pageTitle') },
        ]}
      />

      <StepperCard data-tour="stepper-card">
        <StepperWrap>
          {steps.map((label, idx) => {
            const state = idx < step ? 'done' : idx === step ? 'active' : 'inactive';
            return (
              <React.Fragment key={idx}>
                <StepItem>
                  <StepCircle $s={state}>
                    {state === 'done' ? <CheckSvg /> : idx + 1}
                  </StepCircle>
                  <StepLabel $s={state}>{label}</StepLabel>
                </StepItem>
                {idx < steps.length - 1 && <StepLine $done={idx < step} />}
              </React.Fragment>
            );
          })}
        </StepperWrap>
      </StepperCard>

      <FormCard $wide={step === 1 || step === 2}>
        {step === 0 && (
          <>
            <CardTitle>{t('gap-analysis:form.cardTitle.step1')}</CardTitle>
            <FieldsWrap>
              <FieldWrap data-tour="gap-name">
                <FieldLabel>{t('gap-analysis:form.fields.nameLabel')}</FieldLabel>
                <TextField
                  type="text"
                  fullWidth
                  error={!!errors.name}
                  {...register('name', { required: t('gap-analysis:form.validation.nameRequired') })}
                />
                {errors.name && <ErrorMsg>{errors.name.message}</ErrorMsg>}
              </FieldWrap>
              <FieldWrap data-tour="gap-description">
                <FieldLabel>{t('gap-analysis:form.fields.description')}</FieldLabel>
                <TextField
                  multiline
                  fullWidth
                  rows={3}
                  placeholder={t('gap-analysis:form.fields.descriptionPlaceholder')}
                  {...register('description')}
                />
              </FieldWrap>
            </FieldsWrap>
          </>
        )}

        {step === 1 && (
          <>
            <CardTitle>{t('gap-analysis:form.cardTitle.step2')}</CardTitle>
            <CardSubtitle>{t('gap-analysis:form.cardTitle.step2Sub')}</CardSubtitle>
            <div data-tour="framework-selector">
              <FrameworkSelector
                frameworks={frameworksData}
                selectedFrameworks={selectedFrameworks}
                handleToggle={handleToggle}
              />
              {errors.frameworks && <ErrorMsg>{errors.frameworks.message}</ErrorMsg>}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <CardTitle>{t('gap-analysis:form.cardTitle.step3')}</CardTitle>
            <CardSubtitle>{t('gap-analysis:form.cardTitle.step3Sub')}</CardSubtitle>
            <div data-tour="controls-list">
              <DomainControlsList data={controls} />
            </div>
          </>
        )}

        {step === 3 && (() => {
          const totalControls = controls.reduce((sum, s) => sum + (s.count ?? 0), 0);
          const totalDomains = controls.length;
          return (
            <>
              <CardTitle>{t('gap-analysis:form.cardTitle.step4')}</CardTitle>
              <SummaryList data-tour="review-summary">
                <SummaryRow>
                  <SummaryLabel>{t('gap-analysis:form.summary.nameLabel')}</SummaryLabel>
                  <SummaryValue>{watch('name') || t('gap-analysis:form.summary.notInformed')}</SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>{t('gap-analysis:form.summary.frameworksLabel')}</SummaryLabel>
                  <div>
                    {selectedFrameworks.length > 0 ? (
                      <FwPillsWrap>
                        {selectedFrameworks.map(fw => (
                          <FwPill key={fw._id}>{fw.sourceIdentifier}</FwPill>
                        ))}
                      </FwPillsWrap>
                    ) : (
                      <SummaryValue>{t('gap-analysis:form.summary.noFrameworks')}</SummaryValue>
                    )}
                  </div>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>{t('gap-analysis:form.summary.totalLabel')}</SummaryLabel>
                  <SummaryValue>
                    {t('gap-analysis:form.summary.totalValue', { controls: totalControls, domains: totalDomains })}
                  </SummaryValue>
                </SummaryRow>
              </SummaryList>
            </>
          );
        })()}
      </FormCard>

      <NavRow>
        {step > 0 && (
          <BackBtn type="button" onClick={() => setStep(prev => prev - 1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('gap-analysis:form.backButton')}
          </BackBtn>
        )}
        {step < 3 && (
          <NextBtn type="button" onClick={handleNext}>
            {t('gap-analysis:form.nextButton')}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </NextBtn>
        )}
        {step === 3 && (
          <SaveBtn type="button" data-tour="save-button" onClick={submitGapAnalysis}>
            <CheckSvg />
            {t('gap-analysis:form.saveButton')}
          </SaveBtn>
        )}
      </NavRow>
    </Container>
  );
};
